// Phase C's sky in the sim (seam 4, gate C3): the time-of-day clock and the weather as one kernel module, so both are
// saved, hashed and stepped on the sim's fixed step. The clock (@rpg/world's) moves by the sky table's day length and
// rate and emits `sky:time` at its marks; the weather (@rpg/world's) runs the weather table's schedule on the
// runtime's seeded "weather" stream and emits `sky:weather` when it changes; ground wetness rides along. Any scene adds
// `createSky(...).module` to its kernel game (the sky lab now; the valley at the merge) and `build` once its world
// exists. Debug and script changes (set the hour, force a weather, the rate) go through the module's actions, so they
// land in the sim.
import type { GameModule, World } from "@rpg/kernel";
import type { RngStreams } from "@rpg/runtime";
import {
  advanceTo,
  createClockState,
  createWeatherState,
  hoursPerSecond,
  periodAt,
  setWeather,
  stepClock,
  stepWeather,
  totalHours,
  validateClock,
  validateWeather,
  weatherBlend,
  type ClockConfig,
  type ClockState,
  type WeatherBlend,
  type WeatherRow,
  type WeatherState,
} from "@rpg/world";
import type { SpikeContent } from "../content";
import type { GameEvents } from "../core";

/** The sky's kernel component: the clock and the weather (saved and hashed). */
export const SKY_C = "sky/State";

/** The component's data. */
export interface SkyState {
  clock: ClockState;
  weather: WeatherState;
}

/** The sky's values from the content (the sky row, the weather table). */
export interface SkyContent {
  clock: ClockConfig;
  start: number;
  rate: number;
  weather: string;
  table: WeatherRow[];
}

/** Planted defects for the sky's checks. */
export const SKY_FAULTS = {
  /** The save slice leaves the clock out: a restored save keeps the clock where it was, and the hash differs. */
  skyNotSaved: false,
};

/** Read the sky row `id` (default "valley") and the weather table. */
export function readSkyContent(content: SpikeContent, id = "valley"): SkyContent {
  const row = content.registry.get("sky", id);
  if (!row) throw new Error(`sky: no sky row "${id}"`);
  const table: WeatherRow[] = content.registry.rows("weather").map(({ reason: _r, hours, next, ...w }) => ({ ...w, next: next as Record<string, number>, hours: [hours[0]!, hours[1]!] as const }));
  const clock: ClockConfig = { dayMinutes: row.dayMinutes, marks: row.marks };
  validateClock(clock);
  validateWeather(table);
  return { clock, start: row.start, rate: row.rate, weather: row.weather.id, table };
}

/** The sky in a scene's kernel game. */
export interface Sky {
  module: GameModule<GameEvents>;
  /** The component types the state hash covers. */
  hashed: readonly string[];
  /** Make the sky's entity (a scene's content build calls it once). */
  build(world: World): void;
  /** The live state (read-only by contract: change it through `set`, `weather`, `rate`). */
  state(): Readonly<SkyState>;
  /** The weather's blend now (the look reads it). */
  blend(): WeatherBlend;
  /** Hours into the day now. */
  hours(): number;
  /** The period now (the mark it last passed: dawn, day, dusk, night). */
  period(): string | null;
  readonly content: SkyContent;
}

/** The actions a scene or the handle dispatches (applied at once, as sim state). */
export const SKY_ACTIONS = { set: "sky/set", weather: "sky/weather", rate: "sky/rate", wet: "sky/wet" } as const;

/** Make the sky module from its content, on the scene's RNG streams. */
export function createSky(o: { content: SkyContent; rng: RngStreams }): Sky {
  const c = o.content;
  const rng = o.rng.stream("weather");
  const next = (): number => rng.next();
  let state: SkyState | null = null;
  const live = (): SkyState => {
    if (!state) throw new Error("sky: not built yet");
    return state;
  };

  const module: GameModule<GameEvents> = {
    id: "sky",
    version: "0.1.0",
    // checked declarations (decision 0077): what it emits
    provides: { events: ["sky:time", "sky:weather"] },
    setup(ctx) {
      // early in the step: everything after reads this step's hour and weather
      ctx.registerSystem(
        ({ dt, events }) => {
          const s = live();
          const before = totalHours(s.clock);
          for (const m of stepClock(s.clock, dt, c.clock)) events.emit("sky:time", { mark: m.id, hours: m.at, day: m.day });
          const now = totalHours(s.clock);
          for (const w of stepWeather(s.weather, c.table, now, now - before, next)) events.emit("sky:weather", { from: w.from, to: w.to, hours: w.at });
        },
        { name: "sky: clock and weather", order: -200 },
      );
      ctx.registerSaveSlice({
        serialize: () => {
          const s = live();
          return SKY_FAULTS.skyNotSaved ? { weather: structuredClone(s.weather) } : structuredClone(s);
        },
        deserialize: (data) => {
          const d = data as Partial<SkyState>;
          const s = live();
          if (d.clock) Object.assign(s.clock, d.clock);
          if (d.weather) Object.assign(s.weather, d.weather);
        },
      });
      // set the hour (forward to the next such hour; the marks on the way fire): { hours }
      ctx.registerAction(SKY_ACTIONS.set, (p) => {
        const s = live();
        const { hours } = p as { hours: number };
        for (const m of advanceTo(s.clock, hours, c.clock)) ctx.events.emit("sky:time", { mark: m.id, hours: m.at, day: m.day });
      });
      // force a weather: { id, instant? } (instant: no fade)
      ctx.registerAction(SKY_ACTIONS.weather, (p) => {
        const s = live();
        const { id, instant } = p as { id: string; instant?: boolean };
        const ch = setWeather(s.weather, c.table, id, totalHours(s.clock), next, { instant: instant === true });
        if (ch) ctx.events.emit("sky:weather", { from: ch.from, to: ch.to, hours: ch.at });
      });
      ctx.registerAction(SKY_ACTIONS.rate, (p) => void (live().clock.rate = Math.max(0, (p as { rate: number }).rate)));
      ctx.registerAction(SKY_ACTIONS.wet, (p) => void (live().weather.wet = Math.min(1, Math.max(0, (p as { wet: number }).wet))));
    },
  };

  return {
    module,
    hashed: [SKY_C],
    content: c,
    build(world) {
      const clock = createClockState(c.start, c.rate);
      const weather = createWeatherState(c.table, c.weather, totalHours(clock), next);
      state = { clock, weather };
      // the component holds the same object, so the hash and the save see the live state
      world.addComponent<SkyState>(world.createEntity(), SKY_C, state);
    },
    state: live,
    blend: () => weatherBlend(live().weather, c.table, totalHours(live().clock)),
    hours: () => live().clock.hours,
    period: () => periodAt(c.clock.marks, live().clock.hours),
  };
}

/** Game hours per real second at the sky's rate (for specs that run a day). */
export const skyHoursPerSecond = (sky: Sky): number => hoursPerSecond(sky.content.clock, sky.state().clock.rate);
